import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { SelectButton } from "primereact/selectbutton";
import { Dropdown } from "primereact/dropdown";
import PageContainer from "../../Components/Page/PageContainer";
import { filterNavigationItems, NAVIGATION_ITEMS, ROUTES } from "../../config/routes";
import { useUserAccess } from "../../hooks/useUserAccess";
import { canUseDevOverrides } from "../../utils/devOverrides";
import {
  getDevPreviewRole,
  setDevPreviewEmail,
  setDevPreviewRole,
} from "../../utils/userAccess";
import { moveWidget, resolveOrder } from "./prefs/homePrefs";
import { useHomePreferences } from "./prefs/useHomePreferences";
import { visibleWidgets } from "./widgets/registry";
import { effectiveFilters } from "./widgets/types";
import WidgetCard from "./widgets/WidgetCard";
import { loadManagerOptions } from "./widgets/WidgetFilterDialog";
import { SelectOption } from "./widgets/types";
import "./homepage.css";

const greeting = (date: Date) => {
  const hour = date.getHours();
  return hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
};

const Homepage = () => {
  const access = useUserAccess();
  const { prefs, setOrder, setHidden, setWidgetFilters, resetAll } = useHomePreferences(access.uid);
  const [customiseOpen, setCustomiseOpen] = useState(false);

  const available = useMemo(
    () => visibleWidgets({ role: access.role, tools: access.tools }),
    [access.role, access.tools]
  );
  const byId = useMemo(() => new Map(available.map((w) => [w.id, w])), [available]);

  const order = useMemo(
    () => resolveOrder(available.map((w) => w.id), prefs),
    [available, prefs]
  );
  const shown = order.filter((id) => !prefs.hidden.includes(id));
  const hiddenWidgets = order.filter((id) => prefs.hidden.includes(id)).map((id) => byId.get(id)!);

  const quickLinks = useMemo(
    () =>
      filterNavigationItems(NAVIGATION_ITEMS, { role: access.role, tools: access.tools }).filter(
        (item) => item.path !== ROUTES.HOMEPAGE
      ),
    [access.role, access.tools]
  );

  const firstName = (access.displayName || access.email || "").split(/[\s@.]/)[0];
  const now = new Date();
  const devPreview = canUseDevOverrides(access.realEmail);
  const previewRole = getDevPreviewRole(access.realEmail);
  const [managerOptions, setManagerOptions] = useState<SelectOption[]>([]);

  useEffect(() => {
    if (!devPreview) return;
    loadManagerOptions()
      .then(setManagerOptions)
      .catch((e) => console.error("Failed to load channel managers", e));
  }, [devPreview]);

  return (
    <PageContainer HomeMenuBarprops={{ title: "Home" }}>
      <div className="home-page">
        <header className="home-hero">
          <div>
            <p className="home-hero__date">
              {now.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}
            </p>
            <h1 className="home-hero__title">
              {greeting(now)}
              {firstName ? `, ${firstName.charAt(0).toUpperCase()}${firstName.slice(1)}` : ""}
            </h1>
            {access.previewEmail && (
              <p className="home-hero__preview">
                <i className="pi pi-eye" /> Previewing as {access.previewEmail}
              </p>
            )}
            <p className="home-hero__subtitle">
              {access.isAdmin
                ? "Here's what needs attention across the network."
                : "Here's what needs attention on your channels."}
            </p>
          </div>
          <div className="home-hero__actions">
            {devPreview && (
              <div className="home-dev-preview" title="Localhost only: preview the page as another role">
                <span>View as</span>
                <SelectButton
                  value={previewRole ?? "real"}
                  onChange={(e) => e.value && setDevPreviewRole(e.value === "real" ? null : e.value)}
                  options={[
                    { label: "Me", value: "real" },
                    { label: "User", value: "user" },
                    { label: "Admin", value: "admin" },
                  ]}
                />
                <Dropdown
                  value={access.previewEmail}
                  options={
                    // Keep the current preview visible while options load, or if it isn't in channelSettings.
                    access.previewEmail && !managerOptions.some((o) => o.value === access.previewEmail)
                      ? [{ label: access.previewEmail, value: access.previewEmail }, ...managerOptions]
                      : managerOptions
                  }
                  onChange={(e) => setDevPreviewEmail(e.value || null)}
                  placeholder="as me"
                  filter
                  showClear
                  className="home-dev-preview__manager"
                />
              </div>
            )}
            <Button
              label="Customise"
              icon="pi pi-th-large"
              outlined
              size="small"
              onClick={() => setCustomiseOpen(true)}
            />
          </div>
        </header>

        {quickLinks.length > 0 && (
          <nav className="home-quicklinks" aria-label="Your tools">
            {quickLinks.map((item) => (
              <Link key={item.path} to={item.path} className="home-quicklink">
                <i className={item.icon} />
                <span>{item.label}</span>
              </Link>
            ))}
          </nav>
        )}

        {shown.length === 0 ? (
          <div className="home-empty">
            <i className="pi pi-th-large" />
            <h2>{available.length === 0 ? "No dashboards yet" : "All cards are hidden"}</h2>
            <p>
              {available.length === 0
                ? "Ask an admin for access to a tool and its summary will appear here."
                : "Use Customise to bring cards back."}
            </p>
            {available.length > 0 && (
              <Button label="Customise" icon="pi pi-th-large" onClick={() => setCustomiseOpen(true)} />
            )}
          </div>
        ) : (
          <div className="home-grid">
            {shown.map((id, index) => {
              const def = byId.get(id)!;
              return (
                <WidgetCard
                  key={id}
                  def={def}
                  access={access}
                  filters={effectiveFilters(def, access.isAdmin, prefs.filters[id])}
                  hasCustomFilters={!!prefs.filters[id]}
                  isFirst={index === 0}
                  isLast={index === shown.length - 1}
                  onMove={(direction) => {
                    const nextShown = moveWidget(shown, id, direction);
                    // Keep hidden cards at the end so they return in a stable place.
                    setOrder([...nextShown, ...order.filter((o) => prefs.hidden.includes(o))]);
                  }}
                  onHide={() => setHidden(id, true)}
                  onSaveFilters={(values) => setWidgetFilters(id, values)}
                />
              );
            })}
          </div>
        )}
      </div>

      <Dialog
        header="Customise your home page"
        visible={customiseOpen}
        onHide={() => setCustomiseOpen(false)}
        style={{ width: "min(30rem, 94vw)" }}
        dismissableMask
        resizable={false}
        draggable={false}
      >
        <p className="home-muted mt-0">
          Use the <i className="pi pi-ellipsis-v" /> menu on each card to reorder it, change its filters or
          hide it. Your layout is saved in this browser.
        </p>
        <div className="home-section-title">Hidden cards</div>
        {hiddenWidgets.length === 0 ? (
          <p className="home-muted">No hidden cards.</p>
        ) : (
          <ul className="home-hidden-list">
            {hiddenWidgets.map((w) => (
              <li key={w.id}>
                <span>
                  <i className={w.icon} style={{ color: w.accent }} /> {w.title}
                </span>
                <Button label="Show" icon="pi pi-eye" size="small" text onClick={() => setHidden(w.id, false)} />
              </li>
            ))}
          </ul>
        )}
        <div className="flex justify-content-end mt-4">
          <Button
            label="Reset layout and filters"
            icon="pi pi-refresh"
            severity="secondary"
            outlined
            size="small"
            onClick={() => {
              resetAll();
              setCustomiseOpen(false);
            }}
          />
        </div>
      </Dialog>
    </PageContainer>
  );
};

export default Homepage;
